import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Globe, Crown, Zap, Loader2, DollarSign, XCircle, ArrowUp, ArrowDown, RotateCcw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIER_ORDER = ['free', 'tier_5', 'tier_10', 'tier_15', 'tier_25', 'tier_35'];
const TIER_PRICES = { free: 0, tier_5: 5, tier_10: 10, tier_15: 15, tier_25: 25, tier_35: 35 };
const TIER_NAMES = { free: 'Free', tier_5: 'Adventurer', tier_10: 'Hero', tier_15: 'Champion', tier_25: 'Warrior', tier_35: 'Legend' };

function TierCard({ name, price, tierId, current, models, messages, note, nsfw, highlight, action, prorateCost, onAction, onActionPaypal, processing, processingMethod, disabled, cancelled, pendingDowngrade, expiresAt }) {
  const actionLabel = action === 'upgrade' ? `Upgrade (+$${prorateCost})` :
                      action === 'downgrade' ? 'Downgrade' :
                      action === 'current' ? null : null;

  return (
    <div className={`glass-card p-4 rounded-lg border transition-all ${
      current ? 'border-blue-400/50 bg-blue-400/5' :
      highlight ? 'border-yellow-400/30 hover:border-yellow-400/50' :
      'border-white/10 hover:border-blue-400/30'
    }`} data-testid={`tier-card-${tierId || 'free'}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-lg font-bold text-slate-200">{name}</h3>
            <span className={`text-sm font-mono ${highlight ? 'text-yellow-400' : 'text-blue-400'}`}>{price}</span>
            {current && <span className="px-2 py-0.5 bg-blue-400/20 text-blue-400 text-[10px] rounded-full font-bold">CURRENT</span>}
            {current && cancelled && <span className="px-2 py-0.5 bg-red-400/20 text-red-400 text-[10px] rounded-full font-bold">CANCELLING</span>}
            {pendingDowngrade === tierId && <span className="px-2 py-0.5 bg-amber-400/20 text-amber-400 text-[10px] rounded-full font-bold">PENDING</span>}
          </div>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {models.map((m, i) => (
              <span key={i} className="text-[11px] px-2 py-0.5 rounded-full bg-slate-800/80 text-slate-400 border border-white/5">{m}</span>
            ))}
            {nsfw && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500/15 text-red-400 border border-red-500/20">NSFW</span>}
          </div>
          <div className="flex items-center gap-3 text-xs text-slate-500">
            <span>{messages}</span>
            {note && <span className="text-slate-600">&middot; {note}</span>}
          </div>
          {current && cancelled && expiresAt && (
            <p className="text-xs text-red-400/80 mt-2 font-mono">
              Access until {new Date(expiresAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
          )}
        </div>
        {!current && action && (
          <div className="flex flex-col gap-1.5 ml-3">
            {action === 'upgrade' && (
              <Button
                onClick={() => onAction('stripe')}
                size="sm"
                disabled={processing || disabled}
                className="bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 border border-blue-400/30 text-xs h-8"
                data-testid={`upgrade-stripe-${tierId}`}
              >
                {processing ? <Loader2 className="h-3 w-3 animate-spin" /> : <><ArrowUp className="h-3 w-3 mr-1" /> <DollarSign className="h-3 w-3 mr-1" /> ${prorateCost}</>}
              </Button>
            )}
            {action === 'downgrade' && (
              <Button
                onClick={() => onAction()}
                size="sm"
                disabled={processing || disabled}
                className="bg-orange-500/20 hover:bg-orange-500/30 text-orange-400 border border-orange-500/30 text-xs h-8"
                data-testid={`downgrade-${tierId}`}
              >
                {processing ? <Loader2 className="h-3 w-3 animate-spin" /> : <><ArrowDown className="h-3 w-3 mr-1" /> Downgrade</>}
              </Button>
            )}
            {action === 'subscribe' && (
              <Button
                onClick={() => onAction('stripe')}
                size="sm"
                disabled={processing || disabled}
                className="bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 border border-blue-500/30 text-xs h-8"
                data-testid={`checkout-stripe-${tierId}`}
              >
                {processing ? <Loader2 className="h-3 w-3 animate-spin" /> : <><DollarSign className="h-3 w-3 mr-1" /> Subscribe</>}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Settings() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, language, setLanguage } = useLanguage();
  const { user, logout } = useAuth();
  const [subInfo, setSubInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [processingTier, setProcessingTier] = useState(null);
  const [processingMethod, setProcessingMethod] = useState(null);
  const [messageUsage, setMessageUsage] = useState(null);
  const [messagePacks, setMessagePacks] = useState([]);
  const [buyingPack, setBuyingPack] = useState(null);
  const [buyingMethod, setBuyingMethod] = useState(null);

  const fetchSubscriptionInfo = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/subscription/info`, { withCredentials: true });
      setSubInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUsage = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/usage`, { withCredentials: true });
      setMessageUsage(response.data);
    } catch (error) {
      console.error('Failed to fetch usage:', error);
    }
  }, []);

  const fetchPacks = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/message-packs`, { withCredentials: true });
      setMessagePacks(response.data.packs || []);
    } catch (error) {
      console.error('Failed to fetch packs:', error);
    }
  }, []);

  // Poll payment status after returning from Stripe
  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 8;
    if (attempts >= maxAttempts) {
      setCheckingPayment(false);
      toast.error('Payment verification timed out. Please check your email for confirmation.');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`, { withCredentials: true });
      const data = response.data;

      if (data.payment_status === 'paid') {
        setCheckingPayment(false);
        toast.success(data.message || 'Payment successful!');
        fetchSubscriptionInfo();
        fetchUsage();
        // Clean the URL
        window.history.replaceState({}, '', '/settings');
        return;
      }

      if (data.status === 'expired') {
        setCheckingPayment(false);
        toast.error('Payment session expired. Please try again.');
        window.history.replaceState({}, '', '/settings');
        return;
      }

      // Keep polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      console.error('Payment status check error:', error);
      setCheckingPayment(false);
      toast.error('Error checking payment status.');
    }
  }, [fetchSubscriptionInfo]);

  useEffect(() => {
    fetchSubscriptionInfo();
    fetchUsage();
    fetchPacks();
    // Check if returning from Stripe
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      setCheckingPayment(true);
      pollPaymentStatus(sessionId);
    }
  }, [fetchSubscriptionInfo, pollPaymentStatus, searchParams]);

  const handleUpgrade = async (tierId, method) => {
    setProcessingTier(tierId);
    setProcessingMethod(method);
    try {
      const response = await axios.post(`${API}/subscription/upgrade`, {
        new_tier: tierId,
        payment_method: method,
        origin_url: window.location.origin,
      }, { withCredentials: true });
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upgrade failed');
      setProcessingTier(null);
      setProcessingMethod(null);
    }
  };

  const handleDowngrade = async (tierId) => {
    setProcessingTier(tierId);
    try {
      const response = await axios.post(`${API}/subscription/downgrade`, {
        new_tier: tierId,
      }, { withCredentials: true });
      toast.success(response.data.message);
      fetchSubscriptionInfo();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Downgrade failed');
    } finally {
      setProcessingTier(null);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You\'ll keep access until the end of your current billing period.')) return;
    try {
      const response = await axios.post(`${API}/subscription/cancel`, {}, { withCredentials: true });
      toast.success(response.data.message);
      fetchSubscriptionInfo();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel');
    }
  };

  const handleResubscribe = async () => {
    try {
      const response = await axios.post(`${API}/subscription/resubscribe`, {}, { withCredentials: true });
      toast.success(response.data.message);
      fetchSubscriptionInfo();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resubscribe');
    }
  };

  const handleCancelDowngrade = async () => {
    try {
      const response = await axios.post(`${API}/subscription/cancel-downgrade`, {}, { withCredentials: true });
      toast.success(response.data.message);
      fetchSubscriptionInfo();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel downgrade');
    }
  };

  const handleBuyMatrixCodes = async (packId, method) => {
    setBuyingPack(packId);
    setBuyingMethod(method);
    try {
      const response = await axios.post(`${API}/matrix-codes/buy`, {
        pack_id: packId,
        payment_method: method,
        origin_url: window.location.origin,
      }, { withCredentials: true });
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setBuyingPack(null);
      setBuyingMethod(null);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen py-8 sm:py-12 px-4 sm:px-6" data-testid="settings-page">
      <div className="max-w-3xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          className="text-slate-400 hover:text-blue-400 mb-8"
          data-testid="back-btn"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Dashboard
        </Button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card p-8 rounded-2xl"
        >
          <h1 className="text-4xl font-bold text-blue-400 font-mono mb-8">
            {t('settings.title')}
          </h1>

          {/* Payment Processing Banner */}
          {checkingPayment && (
            <div className="mb-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg flex items-center gap-3">
              <Loader2 className="h-5 w-5 text-blue-400 animate-spin" />
              <p className="text-blue-300">Verifying your payment... This may take a moment.</p>
            </div>
          )}

          {/* User Info */}
          <div className="mb-8 pb-8 border-b border-white/10">
            <h2 className="text-xl font-semibold text-slate-200 mb-4">Profile</h2>
            <div className="flex items-center gap-4">
              {user?.picture && (
                <img src={user.picture} alt={user.name} className="w-16 h-16 rounded-full border-2 border-blue-400/30" />
              )}
              <div>
                <p className="text-slate-200 font-medium text-lg">{user?.name}</p>
                <p className="text-slate-400">{user?.email}</p>
              </div>
            </div>
          </div>

          {/* Language Settings */}
          <div className="mb-8 pb-8 border-b border-white/10">
            <h2 className="text-xl font-semibold text-slate-200 mb-4">
              <Globe className="inline mr-2 h-5 w-5" />
              {t('settings.language')}
            </h2>
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger className="w-full bg-slate-950/50 border-white/10 focus:border-blue-400/50 text-slate-200" data-testid="language-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-white/10">
                <SelectItem value="en" className="text-slate-200 focus:bg-blue-400/20">English</SelectItem>
                <SelectItem value="zh" className="text-slate-200 focus:bg-blue-400/20">中文 (Chinese)</SelectItem>
                <SelectItem value="ru" className="text-slate-200 focus:bg-blue-400/20">Русский (Russian)</SelectItem>
                <SelectItem value="de" className="text-slate-200 focus:bg-blue-400/20">Deutsch (German)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Subscription Management */}
          <div className="mb-8 pb-8 border-b border-white/10">
            <h2 className="text-xl font-semibold text-slate-200 mb-4">
              <Crown className="inline mr-2 h-5 w-5 text-blue-400" />
              Subscription Plans
            </h2>
            
            {loading ? (
              <p className="text-slate-400">Loading...</p>
            ) : subInfo && (
              <>
                {/* Message Usage */}
                {messageUsage && (
                  <div className="glass-card p-4 rounded-lg mb-4" data-testid="message-usage-card">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-slate-300 font-medium">{messageUsage.tier_name}</p>
                      {messageUsage.is_admin && (
                        <span className="px-2 py-0.5 bg-amber-400/20 text-amber-400 text-[10px] rounded-full font-bold">ADMIN</span>
                      )}
                    </div>
                    
                    {/* Standard Messages */}
                    <div className="mb-3">
                      <div className="flex justify-between text-xs text-slate-500 mb-1">
                        <span>Standard (Gemini Flash / DeepSeek)</span>
                        <span>
                          {messageUsage.standard?.limit === -1
                            ? `${messageUsage.standard?.used || 0} used (unlimited)`
                            : `${messageUsage.standard?.used || 0} / ${messageUsage.standard?.limit || 0}`
                          }
                        </span>
                      </div>
                      {messageUsage.standard?.limit !== -1 && (
                        <div className="w-full h-1.5 bg-slate-800 rounded-full">
                          <div className="h-full rounded-full bg-blue-500 transition-all" style={{ width: `${Math.min(((messageUsage.standard?.used || 0) / (messageUsage.standard?.limit || 1)) * 100, 100)}%` }} />
                        </div>
                      )}
                    </div>
                    
                    {/* Premium Messages */}
                    {messageUsage.premium?.limit > 0 && (
                      <div>
                        <div className="flex justify-between text-xs text-slate-500 mb-1">
                          <span className="text-amber-400/80">Premium (Gemini Pro / GPT-5.2 / Claude Opus)</span>
                          <span className="text-amber-400/80">{messageUsage.premium?.used || 0} / {messageUsage.premium?.limit || 0}</span>
                        </div>
                        <div className="w-full h-1.5 bg-slate-800 rounded-full">
                          <div className="h-full rounded-full bg-amber-500 transition-all" style={{ width: `${Math.min(((messageUsage.premium?.used || 0) / (messageUsage.premium?.limit || 1)) * 100, 100)}%` }} />
                        </div>
                      </div>
                    )}
                    {messageUsage.premium?.bonus > 0 && (
                      <p className="text-amber-400 text-xs font-mono mt-2">+ {messageUsage.premium.bonus} bonus Premium messages</p>
                    )}
                  </div>
                )}

                {/* Tier Cards */}
                <div className="space-y-3">
                  {[
                    { name: 'Free', price: 'Free', tierId: 'free', models: ['Gemini Flash', 'DeepSeek V3.2'], messages: '300 standard messages/month', note: 'No premium model access' },
                    { name: 'Adventurer', price: '$5/mo', tierId: 'tier_5', models: ['Unlimited Standard', '100 Premium messages'], messages: '100 Premium/month', note: 'Gemini Pro, GPT-5.2 & Claude Opus' },
                    { name: 'Hero', price: '$10/mo', tierId: 'tier_10', models: ['Unlimited Standard', '200 Premium messages'], messages: '200 Premium/month', note: 'Double the immersion' },
                    { name: 'Champion', price: '$15/mo', tierId: 'tier_15', models: ['Unlimited Standard', '400 Premium messages'], messages: '400 Premium/month', note: 'For dedicated adventurers' },
                    { name: 'Warrior', price: '$25/mo', tierId: 'tier_25', models: ['Unlimited Standard', '750 Premium messages'], messages: '750 Premium/month', note: 'Epic-level campaigns', highlight: true },
                    { name: 'Legend', price: '$35/mo', tierId: 'tier_35', models: ['Unlimited Standard', '1,200 Premium messages'], messages: '1,200 Premium/month', note: 'The ultimate experience', highlight: true },
                  ].map(tier => {
                    const currentTier = subInfo.subscription_tier;
                    const isCurrent = currentTier === tier.tierId;
                    const currentIdx = TIER_ORDER.indexOf(currentTier);
                    const tierIdx = TIER_ORDER.indexOf(tier.tierId);
                    const isCancelled = subInfo.subscription_cancelled;
                    const pendingDowngrade = subInfo.pending_downgrade_tier;
                    
                    let action = null;
                    let prorateCost = 0;
                    if (!isCurrent) {
                      if (currentTier === 'free') {
                        action = 'subscribe';
                      } else if (tierIdx > currentIdx) {
                        action = 'upgrade';
                        prorateCost = (TIER_PRICES[tier.tierId] - TIER_PRICES[currentTier]).toFixed(2);
                      } else if (tierIdx < currentIdx) {
                        action = 'downgrade';
                      }
                    }

                    return (
                      <TierCard
                        key={tier.tierId}
                        {...tier}
                        current={isCurrent}
                        action={action}
                        prorateCost={prorateCost}
                        cancelled={isCancelled}
                        pendingDowngrade={pendingDowngrade}
                        expiresAt={subInfo.subscription_expires_at}
                        onAction={(method) => {
                          if (action === 'subscribe') {
                            handleUpgrade(tier.tierId, 'stripe');
                          } else if (action === 'upgrade') {
                            handleUpgrade(tier.tierId, 'stripe');
                          } else if (action === 'downgrade') {
                            handleDowngrade(tier.tierId);
                          }
                        }}
                        processing={processingTier === tier.tierId}
                        processingMethod={processingMethod}
                        disabled={checkingPayment}
                      />
                    );
                  })}
                </div>

                {/* Cancel / Resubscribe / Cancel Downgrade Actions */}
                {subInfo.subscription_tier !== 'free' && (
                  <div className="mt-4 space-y-2">
                    {subInfo.subscription_cancelled ? (
                      <Button
                        onClick={handleResubscribe}
                        className="w-full bg-green-500/20 hover:bg-green-500/30 text-green-400 border border-green-400/30 font-mono"
                        data-testid="resubscribe-btn"
                      >
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Re-subscribe to {TIER_NAMES[subInfo.subscription_tier]}
                      </Button>
                    ) : subInfo.pending_downgrade_tier ? (
                      <Button
                        onClick={handleCancelDowngrade}
                        className="w-full bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 border border-amber-400/30 font-mono text-sm"
                        data-testid="cancel-downgrade-btn"
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        Cancel downgrade to {TIER_NAMES[subInfo.pending_downgrade_tier]} — keep {TIER_NAMES[subInfo.subscription_tier]}
                      </Button>
                    ) : (
                      <Button
                        onClick={handleCancel}
                        className="w-full bg-red-500/10 hover:bg-red-500/20 text-red-400/70 border border-red-500/20 font-mono text-sm"
                        data-testid="cancel-subscription-btn"
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        Cancel Subscription
                      </Button>
                    )}
                  </div>
                )}

                <div className="mt-4 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                  <p className="text-emerald-400 text-sm">
                    <strong>Tip:</strong> Standard models (Gemini Flash, DeepSeek V3.2) are free for all tiers with paid tiers getting unlimited use. Premium models (Gemini Pro, GPT-5.2, Claude Opus 4.5) deliver richer narratives — subscribe to unlock premium messages, or buy credit packs below!
                  </p>
                </div>
              </>
            )}
          </div>

          {/* Credit Packs */}
          {messageUsage && (
            <div className="mb-8 pb-8 border-b border-white/10">
              <h2 className="text-xl font-semibold text-slate-200 mb-4">
                <Zap className="inline mr-2 h-5 w-5 text-emerald-400" />
                Credit Packs
              </h2>
              <p className="text-slate-400 text-sm mb-2">
                Buy extra messages — <strong className="text-blue-400">Standard Credits</strong> for more Gemini Flash messages, or <strong className="text-amber-400">Premium Credits</strong> for Gemini Pro, GPT-5.2 & Claude Opus messages.
              </p>
              {(messageUsage.standard?.bonus > 0 || messageUsage.premium?.bonus > 0) && (
                <div className="mb-4 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                  {messageUsage.standard?.bonus > 0 && (
                    <p className="text-blue-400 font-mono font-bold" data-testid="bonus-standard-balance">
                      {messageUsage.standard.bonus} bonus Standard messages
                    </p>
                  )}
                  {messageUsage.premium?.bonus > 0 && (
                    <p className="text-amber-400 font-mono font-bold" data-testid="bonus-premium-balance">
                      {messageUsage.premium.bonus} bonus Matrix AI messages
                    </p>
                  )}
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                {messagePacks.map((pack) => (
                  <div key={pack.pack_id} className="glass-card p-4 rounded-lg border border-emerald-500/20 hover:border-emerald-400/40 transition-colors" data-testid={`matrix-pack-${pack.pack_id}`}>
                    <p className="text-slate-200 font-medium text-sm">{pack.label}</p>
                    <p className="text-emerald-400 font-bold text-lg mt-1">${pack.price.toFixed(2)}</p>
                    <p className="text-slate-500 text-xs">${(pack.price / pack.count * 100).toFixed(0)}c per code</p>
                    <div className="flex flex-col gap-1.5 mt-3">
                      <Button
                        size="sm"
                        onClick={() => handleBuyMatrixCodes(pack.pack_id, 'stripe')}
                        className="w-full bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 border border-blue-500/30 text-xs"
                        disabled={buyingPack === pack.pack_id}
                        data-testid={`buy-matrix-stripe-${pack.pack_id}`}
                      >
                        {buyingPack === pack.pack_id && buyingMethod === 'stripe' ? <Loader2 className="h-3 w-3 animate-spin" /> : <><DollarSign className="h-3 w-3 mr-1" /> Buy</>}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Logout */}
          <Button
            onClick={handleLogout}
            variant="destructive"
            className="w-full bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30"
            data-testid="logout-btn"
          >
            {t('settings.logout')}
          </Button>
        </motion.div>
      </div>
    </div>
  );
}
